import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ReactECharts from 'echarts-for-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportView = ({ user, onLogout }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [reportUser, setReportUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const reportRes = await axios.get(`${API}/reports/${reportId}`, config);
      setReport(reportRes.data);

      // Get user details
      const userRes = await axios.get(`${API}/admin/all-users`, config);
      const allUsers = userRes.data;
      const foundUser = allUsers.find(u => u.id === reportRes.data.user_id);
      setReportUser(foundUser);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Asset Allocation Comparison Chart
  const getAssetAllocationChart = () => {
    if (!report?.chart_data) return {};

    const { currentAllocation, recommendedAllocation } = report.chart_data;

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ffd87a',
        borderWidth: 1,
        textStyle: { color: '#1f2937' }
      },
      legend: {
        data: ['Current', 'Recommended'],
        textStyle: { color: '#6b7280' },
        top: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: Object.keys(currentAllocation || {}),
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { color: '#6b7280', rotate: 15 }
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { 
          color: '#6b7280',
          formatter: '{value}%'
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [
        {
          name: 'Current',
          type: 'bar',
          data: Object.values(currentAllocation || {}),
          itemStyle: {
            color: '#ffd87a'
          }
        },
        {
          name: 'Recommended',
          type: 'bar',
          data: Object.values(recommendedAllocation || {}),
          itemStyle: {
            color: '#ffd87a'
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-96">
          <FileText size={64} className="text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Report Not Found</h2>
          <p className="text-gray-600 mb-6">The report you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-ugmonk-yellow hover:bg-ugmonk-yellow text-black rounded-lg"
          >
            Go Back
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[#fffdf0] rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900" style={{fontFamily: "'PerfectNineties', serif"}}>
                Investment Recommendation Report
              </h1>
              <p className="text-sm text-gray-600 mt-1">Generated for {reportUser?.full_name || 'Client'}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            report.status === 'viewed' ? 'bg-[#fffdf0] text-ugmonk-dark' :
            report.status === 'sent' ? 'bg-[#fffdf0] text-ugmonk-dark' :
            'bg-gray-100 text-gray-700'
          }`}>
            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
          </span>
        </div>

        {/* Report Metadata */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <User size={20} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Client</p>
                <p className="text-sm text-gray-900 font-medium">{reportUser?.full_name || 'Unknown'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <Calendar size={20} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Generated On</p>
                <p className="text-sm text-gray-900 font-medium">
                  {new Date(report.created_at).toLocaleDateString('en-IN', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <FileText size={20} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Report Type</p>
                <p className="text-sm text-gray-900 font-medium">Investment Recommendation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Asset Allocation Chart */}
        {report.chart_data && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Asset Allocation Comparison</h3>
            <ReactECharts 
              option={getAssetAllocationChart()} 
              style={{ height: '350px' }}
              opts={{ renderer: 'svg' }}
            />
          </div>
        )}

        {/* Report Content */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-table:text-sm">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ({node, ...props}) => (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full border-collapse border border-gray-300 text-sm" {...props} />
                  </div>
                ),
                thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
                tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200" {...props} />,
                tr: ({node, ...props}) => <tr className="hover:bg-gray-50" {...props} />,
                th: ({node, ...props}) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900 bg-gray-100" {...props} />,
                td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2 text-gray-700" {...props} />,
              }}
            >
              {report.content}
            </ReactMarkdown>
          </div>
        </div>

        {/* Actions */}
        {user?.role === 'rm' && report.status === 'draft' && (
          <div className="flex justify-end gap-3">
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const config = { headers: { Authorization: `Bearer ${token}` } };
                  await axios.post(`${API}/reports/${reportId}/send`, {}, config);
                  alert('Report sent to client!');
                  fetchReport();
                } catch (error) {
                  alert('Failed to send report');
                }
              }}
              className="px-6 py-2 bg-ugmonk-yellow hover:bg-ugmonk-yellow text-black rounded-lg font-medium"
            >
              Send to Client
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportView;
